import z from "zod";

const diPatternErrorTypeValues = [
  "NOT_IMPLEMENTED",
  "NOT_INJECTABLE",
  "UNKNOWN_TOKEN",
  "FINALIZE_NEW_BID_FAILED",
  "RECONCILE_UNFINALIZED_BIDS_FAILED",
  "REVIVE_CHILD_PROJECTS_FAILED",
  "PANDADOC_DOCUMENT_COMPLETION_FAILED",
  "PANDADOC_COMPLETION_STEP_FAILED",
  "COC_INVOICE_DELIVERY_FAILED",
  "SEND_LEAD_STAGE_TO_META_FAILED",
  "DIAL_PRICE_PAGE_LEAD_FAILED",
  "INGEST_INSTANT_FORM_LEAD_FAILED",
  "PARTNER_UPDATE_SEED_CEILING_EXCEEDED",
  "PARTNER_UPDATE_COHORT_TRIPWIRE_EXCEEDED",
  "PARTNER_UPDATE_CONTEXT_NO_BIDS",
  "PARTNER_UPDATE_CONTEXT_ENUM_UNPARSEABLE",
  "OPEN_PHONE_RECONCILIATION_HALTED",
  "CALL_MEASUREMENT_SCORING_FAILED",
] as const;

const diPatternErrorTypeSchema = z.enum(diPatternErrorTypeValues);
export const DiPatternErrorType = diPatternErrorTypeSchema.enum;
type DiPatternErrorType = z.infer<typeof diPatternErrorTypeSchema>;

type ErrorDetails = {
  sentryDescription: string;
};

export type DiPatternErrors =
  | {
      errorType: typeof DiPatternErrorType.NOT_IMPLEMENTED;
      data: {
        method?: string;
      };
    }
  | {
      errorType: typeof DiPatternErrorType.NOT_INJECTABLE;
      data: {
        constructorName?: string;
      };
    }
  | {
      errorType: typeof DiPatternErrorType.UNKNOWN_TOKEN;
      data: {
        token?: string;
      };
    }
  | {
      errorType: typeof DiPatternErrorType.FINALIZE_NEW_BID_FAILED;
      data: {
        bidProjectId: number;
        runId: string;
        originalErrorName: string;
        originalErrorMessage: string;
      };
    }
  | {
      errorType: typeof DiPatternErrorType.RECONCILE_UNFINALIZED_BIDS_FAILED;
      data: {
        runId: string;
        originalErrorName: string;
        originalErrorMessage: string;
      };
    }
  | {
      errorType: typeof DiPatternErrorType.REVIVE_CHILD_PROJECTS_FAILED;
      data: {
        bidProjectId: number;
        runId: string;
        originalErrorName: string;
        originalErrorMessage: string;
      };
    }
  | {
      errorType: typeof DiPatternErrorType.COC_INVOICE_DELIVERY_FAILED;
      data: {
        bidId: number;
        runId: string;
        originalErrorName: string;
        originalErrorMessage: string;
      };
    }
  | {
      errorType: typeof DiPatternErrorType.PANDADOC_DOCUMENT_COMPLETION_FAILED;
      data: {
        documentId: string;
        runId: string;
        originalErrorName: string;
        originalErrorMessage: string;
      };
    }
  | {
      errorType: typeof DiPatternErrorType.DIAL_PRICE_PAGE_LEAD_FAILED;
      data: {
        marketingLeadId: string;
        runId: string;
        originalErrorName: string;
        originalErrorMessage: string;
      };
    }
  | {
      errorType: typeof DiPatternErrorType.SEND_LEAD_STAGE_TO_META_FAILED;
      data: {
        marketingLeadStageId: string;
        runId: string;
        originalErrorName: string;
        originalErrorMessage: string;
      };
    }
  | {
      errorType: typeof DiPatternErrorType.INGEST_INSTANT_FORM_LEAD_FAILED;
      data: {
        metaLeadId: string;
        runId: string;
        originalErrorName: string;
      };
    }
  | {
      errorType: typeof DiPatternErrorType.PANDADOC_COMPLETION_STEP_FAILED;
      data: {
        documentId: string;
        bidId: number;
        failedSteps: string;
      };
    }
  | {
      errorType: typeof DiPatternErrorType.PARTNER_UPDATE_SEED_CEILING_EXCEEDED;
      data: { seedSize: number; ceiling: number };
    }
  | {
      errorType: typeof DiPatternErrorType.PARTNER_UPDATE_COHORT_TRIPWIRE_EXCEEDED;
      data: { cohortSize: number; tripwire: number };
    }
  | {
      errorType: typeof DiPatternErrorType.PARTNER_UPDATE_CONTEXT_NO_BIDS;
      data: { conversationId: string };
    }
  | {
      errorType: typeof DiPatternErrorType.PARTNER_UPDATE_CONTEXT_ENUM_UNPARSEABLE;
      data: { conversationId: string; field: string; value: string };
    }
  | {
      errorType: typeof DiPatternErrorType.OPEN_PHONE_RECONCILIATION_HALTED;
      data: {
        haltedReason: "resolve-cap" | "consecutive-errors";
        resolved: number;
        markedDoneInOpenPhone: number;
        erroredNumbers: number;
        changeGroupId: string;
      };
    }
  | {
      errorType: typeof DiPatternErrorType.CALL_MEASUREMENT_SCORING_FAILED;
      data: { phoneCallId: string; measurementRunGroup: string; failureKind: string; statusCode: number | null };
    };

export const diPatternErrorDetails: Record<DiPatternErrorType, ErrorDetails> = {
  [DiPatternErrorType.NOT_IMPLEMENTED]: {
    sentryDescription: "DI repository/service method is not yet implemented (scaffold stub)",
  },
  [DiPatternErrorType.NOT_INJECTABLE]: {
    sentryDescription: "Class is not marked as @Injectable",
  },
  [DiPatternErrorType.UNKNOWN_TOKEN]: {
    sentryDescription: "Unknown token encountered in dependency injection",
  },
  [DiPatternErrorType.FINALIZE_NEW_BID_FAILED]: {
    sentryDescription:
      "Durable finalize-new-bid Inngest function exhausted retries; the bid is orphaned (missing tasks/thread/region roles)",
  },
  [DiPatternErrorType.RECONCILE_UNFINALIZED_BIDS_FAILED]: {
    sentryDescription:
      "reconcile-unfinalized-bids cron exhausted retries; orphaned bids may not be re-healed this tick",
  },
  [DiPatternErrorType.REVIVE_CHILD_PROJECTS_FAILED]: {
    sentryDescription:
      "Durable revive-child-projects Inngest function exhausted retries; cascade-canceled children stay canceled until the stranded-children gut check flags the bid",
  },
  [DiPatternErrorType.PANDADOC_DOCUMENT_COMPLETION_FAILED]: {
    sentryDescription:
      "Durable handle-pandadoc-document-completion Inngest function ended with failed fatal steps; the error message carries documentId, bidId, and the failed step names",
  },
  [DiPatternErrorType.COC_INVOICE_DELIVERY_FAILED]: {
    sentryDescription:
      "Durable send-coc-invoice-delivery Inngest function exhausted retries; the Certificate of Completion invoice was not delivered to the partner",
  },
  [DiPatternErrorType.DIAL_PRICE_PAGE_LEAD_FAILED]: {
    sentryDescription:
      "Durable dial-price-page-lead Inngest function failed; the lead was routed to Remi, so nobody calls it unless a setter does",
  },
  [DiPatternErrorType.SEND_LEAD_STAGE_TO_META_FAILED]: {
    sentryDescription:
      "Durable send-lead-stage-to-meta Inngest function exhausted retries; the stage row keeps its claim until the stale window lapses and the hourly sweep re-emits it",
  },
  [DiPatternErrorType.INGEST_INSTANT_FORM_LEAD_FAILED]: {
    sentryDescription:
      "Durable ingest-instant-form-lead Inngest function failed after its retries or on a fatal Graph error; the lead was not stored, so rerun the run in Inngest",
  },
  [DiPatternErrorType.PANDADOC_COMPLETION_STEP_FAILED]: {
    sentryDescription:
      "Best-effort steps of handle-pandadoc-document-completion exhausted retries; the run still succeeded, so this capture is the only Sentry signal",
  },
  PARTNER_UPDATE_SEED_CEILING_EXCEEDED: { sentryDescription: "Partner update cohort seed exceeded its ceiling" },
  PARTNER_UPDATE_COHORT_TRIPWIRE_EXCEEDED: { sentryDescription: "Partner update cohort exceeded its tripwire" },
  PARTNER_UPDATE_CONTEXT_NO_BIDS: { sentryDescription: "No bids attached to the partner update conversation" },
  PARTNER_UPDATE_CONTEXT_ENUM_UNPARSEABLE: {
    sentryDescription: "Partner update context read an enum value outside its schema",
  },
  OPEN_PHONE_RECONCILIATION_HALTED: {
    sentryDescription: "OpenPhone conversation reconciliation halted on its resolve cap or consecutive line errors",
  },
  CALL_MEASUREMENT_SCORING_FAILED: {
    sentryDescription: "Setter call measurement scoring failed",
  },
};
